import React, { useEffect, useState, useRef, useImperativeHandle, useMemo, forwardRef, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Toolbar, { Item } from 'devextreme-react/toolbar';
import Button from 'devextreme-react/button';
import stringify from '../../utils/stableStringify';

import AntSwitchStore from './AntSwitchStore';
import AntSwitchEmployee from './AntSwitchEmployee';
import AntEmployeeShiftCheckin from './AntEmployeeShiftCheckin';
import AntEmployeeShiftCheckout from './AntEmployeeShiftCheckout';
import AntUserPanel from './AntUserPanel';
import AntCommandPalette from '../AntCommandPalette';
import AntQuickCreateMenu from './AntQuickCreateMenu';

import './index.scss';
import { Template } from 'devextreme-react/core/template';

import AntLocalization from '../../utils/AntLocalization';
import AntCondition from '../../utils/AntCondition';
import AntAuth from '../../utils/AntAuth';
import { AntGlobal, AntSession } from '../../utils/AntContext';
import cs from '../../utils/const';

export default (props) => {
    const appGlobal = AntGlobal();
    const session = AntSession();
    const navigate = useNavigate();

    const title = appGlobal.appInfo && appGlobal.appInfo.title ? appGlobal.appInfo.title : '';
    const logoWithBrand =
        appGlobal.appAssets && appGlobal.appAssets.images.logoWithBrandWhite ? appGlobal.appAssets.images.logoWithBrandWhite : undefined;
    const imgPOSIcon = appGlobal.appAssets ? appGlobal.appAssets.icons.icon_sale : null;
    const imgAccountingIcon = appGlobal.appAssets ? appGlobal.appAssets.icons.icon_general_ledger : null;
    const imgOperationIcon = appGlobal.appAssets ? appGlobal.appAssets.icons.icon_dashboard : null;

    const onPOSModeClick = useCallback(() => {
        var uiData = localStorage.getItem(cs.System_Code + '-ui-states');
        if (uiData) uiData = JSON.parse(uiData);
        if (!uiData) uiData = {};

        localStorage.setItem(
            cs.System_Code + '-ui-states',
            String(
                stringify({
                    ...uiData,
                    mode: 'POSUI'
                })
            )
        );
        AntAuth.refreshToken(false, null, () => {
            if (
                AntCondition.isVisible(null, {
                    visible: {
                        whenUserScreens: ['ARItemStatuses']
                    }
                })
            )
                navigate('/pos/AROrder');
            else if (
                AntCondition.isVisible(null, {
                    visible: {
                        whenUserScreens: ['ARServeCookings']
                    }
                })
            )
                navigate('/pos/ARServeCooking');
            else if (
                AntCondition.isVisible(null, {
                    visible: {
                        whenUserScreens: ['ARServeServices']
                    }
                })
            )
                navigate('/pos/ARServeService');
            else if (
                AntCondition.isVisible(null, {
                    visible: {
                        whenUserScreens: ['ARAppointments']
                    }
                })
            )
                navigate('/pos/ARAppointment');
            else if (
                AntCondition.isVisible(null, {
                    visible: {
                        whenUserScreens: ['ARRentalRoomBookings']
                    }
                })
            )
                navigate('/pos/ARRentalRoomBooking');
            else if (
                AntCondition.isVisible(null, {
                    visible: {
                        whenUserScreens: ['HRWorkingAttendances']
                    }
                })
            )
                navigate('/pos/HRWorkingAttendance');

            window.location.reload();
        });
    }, []);

    // Chuyển không gian Vận hành ↔ Kế toán: chỉ ở FE (ui-states.mode = 'ACCUI' | 'WebUI'), tải lại trang để dựng lại thực đơn.
    const workspaceMode = useMemo(() => AntAuth.getWorkspaceMode(), []);

    const showWorkspaceSwitch = useMemo(() => AntCondition.canUseAccounting() && !AntAuth.isWorkspaceModeLocked(), []);

    const onWorkspaceModeClick = useCallback(() => {
        const target = workspaceMode === 'Accounting' ? 'Operation' : 'Accounting';
        var uiData = localStorage.getItem(cs.System_Code + '-ui-states');
        if (uiData) uiData = JSON.parse(uiData);
        if (!uiData) uiData = {};

        localStorage.setItem(cs.System_Code + '-ui-states', String(stringify({ ...uiData, mode: target === 'Accounting' ? 'ACCUI' : 'WebUI' })));

        let path = '/home';
        if (target === 'Accounting') {
            const first = (appGlobal.accountingNavigation || []).find((item) => item && item.path && AntCondition.isVisible(null, item));
            if (first) path = first.path;
        }
        navigate(path);
        window.location.reload();
    }, [workspaceMode]);

    const showPOS = useMemo(() => {
        // Điều kiện POS gom về AntCondition.canUsePOS() — dùng chung với nút chuyển POS trên AntVoucher
        // để 2 nút luôn đồng nhất. Màn POS dự kiến mở rộng (CRMLeads/ICIssues/LGTransports/...) chỉnh tại helper.
        return AntCondition.canUsePOS();
    }, []);

    const showSwitchStore = useMemo(() => {
        let userSettings = AntAuth.getSessionInfo('user_settings');
        let storeCodes = userSettings.access_store_codes;
        let permMode = userSettings.DataPermMode_AR;
        let isValid = permMode !== 'NONE' && (permMode === 'ALL' || (storeCodes instanceof Array && storeCodes.length > 1));
        return isValid;
    }, []);

    const showSwitchEmployee = useMemo(() => {
        let empCodes = AntAuth.getSessionInfo('employee_codes');
        let isValid = empCodes instanceof Array && empCodes.length > 1;
        return isValid;
    }, []);

    // HRM tắt → 1 user = 1 ca, không phụ thuộc empCodes.length > 1.
    // Đồng nhất với POS new (TopBar): Vào/Cắt Ca chỉ hiện khi user được bật cờ
    // manages_work_shift (admin tick trên ACUserAccount). BE đã AND sẵn AR+POS module.
    const showEmployeeCheckin = useMemo(() => {
        let manages = Number(AntAuth.getSessionInfo('manages_work_shift')) === 1;
        if (!manages) return false;
        let empCode = AntAuth.getSessionInfo('employee_code');
        let empSessionStatus = AntAuth.getSessionInfo('employee_session_status');
        let empCodes = AntAuth.getSessionInfo('employee_codes');
        let busSettings = AntAuth.getSessionInfo('bus_settings') || {};
        let useHRM = Number(busSettings.use_Module_HRM) === 1;
        if (!useHRM) return empSessionStatus !== 'InProgress';
        return empCodes instanceof Array && empCodes.length > 1 && (empSessionStatus !== 'InProgress' || !empCode);
    }, []);

    const showEmployeeCheckout = useMemo(() => {
        let manages = Number(AntAuth.getSessionInfo('manages_work_shift')) === 1;
        if (!manages) return false;
        let empCode = AntAuth.getSessionInfo('employee_code');
        let empSessionStatus = AntAuth.getSessionInfo('employee_session_status');
        let empCodes = AntAuth.getSessionInfo('employee_codes');
        let busSettings = AntAuth.getSessionInfo('bus_settings') || {};
        let useHRM = Number(busSettings.use_Module_HRM) === 1;
        if (!useHRM) return empSessionStatus === 'InProgress';
        return empCodes instanceof Array && empCodes.length > 1 && empSessionStatus === 'InProgress' && empCode;
    }, []);

    return (
        <header className={'ant-main-header'}>
            <Toolbar>
                {/* <Item visible={props.menuToggleEnabled} location={'before'} widget={'dxButton'} cssClass={'ant-main-header-menu-button'}>
                    {props.menuToggleEnabled && (
                        <Button
                            icon={
                                logoOnly
                                    ? logoOnly
                                    : "<svg role='img' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'><title>Shopify icon</title><path d='M15.337 23.979l7.216-1.561s-2.604-17.613-2.625-17.73c-.018-.116-.114-.192-.211-.192s-1.929-.136-1.929-.136-1.275-1.274-1.439-1.411c-.045-.037-.075-.057-.121-.074l-.914 21.104h.023zM11.71 11.305s-.81-.424-1.774-.424c-1.447 0-1.504.906-1.504 1.141 0 1.232 3.24 1.715 3.24 4.629 0 2.295-1.44 3.76-3.406 3.76-2.354 0-3.54-1.465-3.54-1.465l.646-2.086s1.245 1.066 2.28 1.066c.675 0 .975-.545.975-.932 0-1.619-2.654-1.694-2.654-4.359-.034-2.237 1.571-4.416 4.827-4.416 1.257 0 1.875.361 1.875.361l-.945 2.715-.02.01zM11.17.83c.136 0 .271.038.405.135-.984.465-2.064 1.639-2.508 3.992-.656.213-1.293.405-1.889.578C7.697 3.75 8.951.84 11.17.84V.83zm1.235 2.949v.135c-.754.232-1.583.484-2.394.736.466-1.777 1.333-2.645 2.085-2.971.193.501.309 1.176.309 2.1zm.539-2.234c.694.074 1.141.867 1.429 1.755-.349.114-.735.231-1.158.366v-.252c0-.752-.096-1.371-.271-1.871v.002zm2.992 1.289c-.02 0-.06.021-.078.021s-.289.075-.714.21c-.423-1.233-1.176-2.37-2.508-2.37h-.115C12.135.209 11.669 0 11.265 0 8.159 0 6.675 3.877 6.21 5.846c-1.194.365-2.063.636-2.16.674-.675.213-.694.232-.772.87-.075.462-1.83 14.063-1.83 14.063L15.009 24l.927-21.166z'/></svg>"
                            }
                            stylingMode="text"
                            onClick={props.toggleMenu}
                        />
                    )}
                </Item> */}
                {/* {!props.menuOpened && logoOnly && ( */}
                {!props.menuOpened && logoWithBrand && (
                    <Item location={'before'}>
                        <Button
                            // className={'ant-main-header-logo-button'}

                            className={'ant-main-header-logo-button ant-main-header-logo-with-brand'}
                            onClick={props.toggleMenu}
                            type={'normal'}
                            stylingMode="text"
                            hoverStateEnabled={false}
                            activeStateEnabled={false}
                            focusStateEnabled={false}
                        >
                            {/* <img src={logoOnly} style={{ width: '26px', marginLeft: '5px', padding: '4px', paddingTop: '5px' }} /> */}
                            <img src={logoWithBrand} style={{ width: '60px', marginLeft: '10px' }} />
                        </Button>
                    </Item>
                )}
                {props.menuOpened && logoWithBrand && (
                    <Item location={'before'}>
                        <Button
                            className={'ant-main-header-logo-button ant-main-header-logo-with-brand ant-main-header-logo-open'}
                            onClick={props.toggleMenu}
                            type={'normal'}
                            stylingMode="text"
                            hoverStateEnabled={false}
                            activeStateEnabled={false}
                            focusStateEnabled={false}
                        >
                            <img src={logoWithBrand} style={{ width: '86px', marginLeft: '4px' }} />
                        </Button>
                    </Item>
                )}
                {!logoWithBrand && <Item location={'before'} className={'ant-main-header-title'} text={appInfo.title} visible={!!appInfo.title} />}

                {/* {showSwitchStore && (
                    <Item location={'before'} locateInMenu={'auto'}>
                        <AntSwitchStore />
                    </Item>
                )} */}
                {/* <Item location={'after'} locateInMenu={'never'}>
                    <AntCommandPalette />
                </Item> */}
                {/* {showSwitchEmployee && (
                    <Item location={'before'} locateInMenu={'auto'}>
                        <AntSwitchEmployee />
                    </Item>
                )} */}
                {showEmployeeCheckin && (
                    <Item location={'after'} locateInMenu={'auto'}>
                        <AntEmployeeShiftCheckin visible={true} />
                    </Item>
                )}
                {showEmployeeCheckout && (
                    <Item location={'after'} locateInMenu={'auto'}>
                        <AntEmployeeShiftCheckout />
                    </Item>
                )}
                {workspaceMode === 'Accounting' && (
                    <Item location={'after'} locateInMenu={'never'}>
                        <AntQuickCreateMenu />
                    </Item>
                )}
                {showWorkspaceSwitch && (
                    <Item location={'after'} locateInMenu={'never'}>
                        <Button
                            className="ant-main-header-pos-mode"
                            type="normal"
                            stylingMode="text"
                            hoverStateEnabled={false}
                            activeStateEnabled={false}
                            onClick={onWorkspaceModeClick}
                        >
                            <img src={workspaceMode === 'Accounting' ? imgOperationIcon : imgAccountingIcon}></img>
                            {String(
                                AntLocalization.translateText(
                                    workspaceMode === 'Accounting'
                                        ? { vi: 'Vận hành', en: 'Operations', fr: 'Opérations', es: 'Operaciones', id: 'Operasional', ja: '業務', zh: '业务', ko: '업무', hi: 'संचालन', ar: 'العمليات' }
                                        : { vi: 'Kế toán', en: 'Accounting', fr: 'Comptabilité', es: 'Contabilidad', id: 'Akuntansi', ja: '会計', zh: '会计', ko: '회계', hi: 'लेखांकन', ar: 'المحاسبة' }
                                )
                            ).toUpperCase()}
                        </Button>
                    </Item>
                )}
                {showPOS && (
                    <Item location={'after'} locateInMenu={'never'}>
                        <Button
                            className="ant-main-header-pos-mode"
                            type="normal"
                            stylingMode="text"
                            // focusStateEnabled={false}
                            hoverStateEnabled={false}
                            activeStateEnabled={false}
                            onClick={onPOSModeClick}
                        >
                            <img src={imgPOSIcon}></img>
                            {String(
                                AntLocalization.translateText({ vi: 'Máy bán hàng', en: 'POS Layout', fr: 'Interface PDV', es: 'Interfaz POS', id: 'Tata Letak POS', ja: 'POSレイアウト', zh: 'POS布局', ko: 'POS 레이아웃', hi: 'POS लेआउट', ar: 'تخطيط نقطة البيع' })
                            ).toUpperCase()}
                        </Button>
                    </Item>
                )}
                <Item location={'after'} locateInMenu={'auto'}>
                    <AntUserPanel menuItems={props.userMenuItems} menuMode={'context'} />
                </Item>
            </Toolbar>
        </header>
    );
};
